import React, { useEffect, useMemo, useState } from "react";
import {
  BrowserRouter,
  Link,
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  DollarOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  HistoryOutlined,
  HomeOutlined,
  MenuOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import "./PrepDeckApp.css";

const STORAGE_KEY = "prepdeck.savedPlans.v1";

const DEFAULT_INPUT = "";
const INPUT_PLACEHOLDER =
  "Paste the current order in normal kitchen formatting. Include any count, budget, menu, service time, pickup notes, or constraints you have.";

function safeReadPlans() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

function safeWritePlans(plans) {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(plans));
}

function storageAvailable() {
  try {
    const key = "prepdeck.storage.check";
    window.localStorage.setItem(key, "ok");
    window.localStorage.removeItem(key);
    return true;
  } catch (error) {
    return false;
  }
}

function moneyFromText(text) {
  const match = text.match(/(?:budget|spend|cap|target|price|cost)?[^$]{0,20}\$([0-9][0-9,]*(?:\.[0-9]{1,2})?)/i);
  if (!match) return null;
  return Number(match[1].replace(/,/g, ""));
}

function guestCountFromText(text) {
  const match = text.match(/([0-9][0-9,]*(?:\.[0-9]+)?)\s*(?:guests|people|covers|attendees|pax|servings|meals)/i);
  if (!match) return null;
  return Math.max(1, Math.round(Number(match[1].replace(/,/g, ""))));
}

function extractLabel(text, label) {
  const line = text
    .split(/\n+/)
    .find((entry) => entry.trim().toLowerCase().startsWith(`${label}:`));
  return line ? line.split(":").slice(1).join(":").trim() : "";
}

function extractEventName(text) {
  const explicit = extractLabel(text, "event") || extractLabel(text, "client");
  if (explicit) return explicit.replace(/\s*,\s*(?:january|february|march|april|may|june|july|august|september|october|november|december).*/i, "");
  const firstLine = text.split(/\n+/).map((line) => line.trim()).find(Boolean);
  return firstLine ? firstLine.slice(0, 72) : "Untitled prep plan";
}

function extractServiceDate(text) {
  const monthMatch = text.match(/\b(?:jan|january|feb|february|mar|march|apr|april|may|jun|june|jul|july|aug|august|sep|sept|september|oct|october|nov|november|dec|december)\.?\s+\d{1,2}(?:,\s*\d{4})?(?:\s*(?:at|@)\s*\d{1,2}(?::\d{2})?\s*(?:am|pm)?)?/i);
  if (monthMatch) return monthMatch[0].replace(/\s+/g, " ");
  const isoMatch = text.match(/\b\d{4}-\d{2}-\d{2}(?:\s+\d{1,2}:\d{2})?\b/);
  if (isoMatch) return isoMatch[0];
  return "Date not found";
}

function splitItems(value) {
  return value
    .replace(/^menu\s*:/i, "")
    .split(/[,;|]+|\s+-\s+|\n+/)
    .map((item) => item.trim().replace(/^[-*]\s*/, ""))
    .filter((item) => item.length > 2)
    .filter((item) => !/^(event|guests|budget|constraints|notes|pickup|delivery)\s*:/i.test(item));
}

function extractMenuItems(text) {
  const labeled = extractLabel(text, "menu") || extractLabel(text, "items") || extractLabel(text, "order");
  let items = labeled ? splitItems(labeled) : [];
  if (items.length > 0) return items.slice(0, 9);

  const lines = text
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);

  items = lines
    .filter((line) => /^[-*]/.test(line) || /\b(chicken|beef|pork|salmon|fish|salad|bowl|pasta|rice|quinoa|taco|slider|dessert|cookie|cake|greens|sandwich|soup|tray|platter)\b/i.test(line))
    .flatMap(splitItems);

  return Array.from(new Set(items)).slice(0, 9);
}

function extractConstraints(text) {
  const labeled = extractLabel(text, "constraints") || extractLabel(text, "notes") || extractLabel(text, "dietary");
  const flags = [];
  const patterns = [
    ["Nut-free", /\bnut[-\s]?free|\bpeanut|\btree nut/i],
    ["Vegan count", /\bvegan|plant[-\s]?based/i],
    ["Gluten-free", /\bgluten[-\s]?free|\bceliac/i],
    ["Dairy-free", /\bdairy[-\s]?free|\blactose/i],
    ["Hot holding", /\bhot box|hot holding|warmer|chafing/i],
    ["Pickup timing", /\bpickup|load(?:ing)? dock|delivery|drop[-\s]?off/i],
    ["Staffing pressure", /\bone cook|short[-\s]?staffed|crew|driver|runner/i],
  ];

  patterns.forEach(([label, pattern]) => {
    if (pattern.test(text)) flags.push(label);
  });

  if (labeled) {
    labeled
      .split(/[,;]+/)
      .map((item) => item.trim())
      .filter(Boolean)
      .slice(0, 5)
      .forEach((item) => flags.push(item));
  }

  return Array.from(new Set(flags)).slice(0, 8);
}

function detectServiceStyle(text) {
  if (/boxed|individual|grab[-\s]?and[-\s]?go/i.test(text)) return "Individually packed";
  if (/buffet|chafer|chafing/i.test(text)) return "Buffet service";
  if (/platter|family[-\s]?style/i.test(text)) return "Platter service";
  if (/pickup|carryout|takeout/i.test(text)) return "Pickup handoff";
  return "Catered service";
}

function categorizeItem(item) {
  if (/\b(chicken|beef|pork|salmon|fish|shrimp|turkey|ham|tofu|mushroom)\b/i.test(item)) return "Hot line";
  if (/\b(salad|greens|slaw|fruit|cold|wrap)\b/i.test(item)) return "Cold prep";
  if (/\b(cookie|cake|brownie|dessert|bite|bar|pastry)\b/i.test(item)) return "Bake and finish";
  if (/\b(rice|quinoa|pasta|potato|beans|grain)\b/i.test(item)) return "Batch sides";
  return "Expo pack";
}

function buildStations(items, guestCount) {
  const stationMap = items.reduce((groups, item) => {
    const category = categorizeItem(item);
    const existing = groups[category] || [];
    return { ...groups, [category]: [...existing, item] };
  }, {});

  const stations = Object.entries(stationMap).map(([name, stationItems], index) => ({
    id: `${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${index}`,
    name,
    owner: ["Lead cook", "Cold lead", "Pastry lead", "Expo lead"][index % 4],
    output: stationItems.join(", "),
    target: `${Math.ceil(guestCount * 1.08)} portions with 8% buffer`,
    check: name === "Expo pack" ? "Label, count, and stage by drop window" : "Batch, chill or hold, then mark completion",
  }));

  if (stations.length === 0) {
    return [
      {
        id: "intake-review",
        name: "Order review",
        owner: "Kitchen lead",
        output: "Menu not clearly listed",
        target: `${Math.ceil(guestCount * 1.08)} total portions estimated`,
        check: "Confirm menu lines before production begins",
      },
    ];
  }

  return stations;
}

function buildTimeline(items, constraints, style) {
  const hotItems = items.filter((item) => categorizeItem(item) === "Hot line").length;
  const coldItems = items.filter((item) => categorizeItem(item) === "Cold prep").length;
  const bakeItems = items.filter((item) => categorizeItem(item) === "Bake and finish").length;
  const pickupPressure = constraints.some((item) => /pickup|dock|delivery|handoff/i.test(item));

  return [
    {
      time: "T-72h",
      owner: "Manager",
      task: "Lock menu, count, allergens, pack style, and service window from the pasted order.",
      reason: "Prevents late replanning after purchasing starts.",
    },
    {
      time: "T-48h",
      owner: "Purchasing",
      task: `Buy against ${items.length || "unclear"} menu lines with proteins and allergen-safe packaging checked first.`,
      reason: "The largest failure mode is a missing primary ingredient or safe container.",
    },
    {
      time: "T-24h",
      owner: "Prep lead",
      task: `Batch cold and shelf-stable work; reserve labeled space for ${coldItems || 1} cold-prep station set.`,
      reason: "Creates visible progress before the service-day rush.",
    },
    {
      time: "T-8h",
      owner: "Hot line",
      task: `Start hot production for ${hotItems || 1} hot item group and confirm hold equipment capacity.`,
      reason: "Hot food needs the most recovery time if equipment or yield slips.",
    },
    {
      time: "T-3h",
      owner: "Expo",
      task: `${style} staging: labels, guest-count ticks, allergen separation, and packout table map.`,
      reason: "Turns prep into a shippable order instead of a kitchen-only plan.",
    },
    {
      time: "T-90m",
      owner: "Manager",
      task: pickupPressure ? "Verify dock, pickup crew, driver contact, and replacement route." : "Confirm delivery contact, arrival buffer, and final count.",
      reason: "This is the last low-cost moment to fix handoff risk.",
    },
    {
      time: "T-30m",
      owner: "Expo",
      task: `Final count, temperature log, allergen tag sweep, and ${bakeItems || 1} dessert or garnish check.`,
      reason: "Catches visible defects before the customer sees them.",
    },
  ];
}

function buildShortageWatch(items, constraints, guestCount) {
  const watch = [
    {
      item: "Primary portions",
      severity: guestCount > 150 ? "High" : "Medium",
      trigger: `${Math.ceil(guestCount * 1.08)} portions required with buffer`,
      action: "Count proteins and main meal containers before side prep begins.",
    },
    {
      item: "Labels and allergen markers",
      severity: constraints.some((item) => /nut|vegan|gluten|dairy|allergen/i.test(item)) ? "High" : "Medium",
      trigger: constraints.length ? constraints.join(", ") : "No explicit dietary constraints found",
      action: "Stage separate label colors and hold allergen-sensitive meals away from the main pack line.",
    },
    {
      item: "Packout space",
      severity: items.length >= 5 ? "High" : "Medium",
      trigger: `${items.length || "Unclear"} menu lines across ${new Set(items.map(categorizeItem)).size || 1} station groups`,
      action: "Reserve one clean table for completed packs only; do not mix uncounted prep with finished trays.",
    },
  ];

  if (constraints.some((item) => /hot box|hot holding|warmer/i.test(item))) {
    watch.unshift({
      item: "Hot holding capacity",
      severity: "High",
      trigger: "Equipment limitation found in the order notes",
      action: "Move cold items out of the hot-line path and borrow or rent holding capacity before service day.",
    });
  }

  return watch.slice(0, 5);
}

function buildNextActions(plan) {
  const actions = [
    `Confirm the ${plan.intake.guestCount}-person count and service time with the customer.`,
    "Assign station owners and mark one person as final packout checker.",
    "Print or share the kitchen brief before purchasing starts.",
  ];

  if (plan.metrics.riskScore >= 70) {
    actions.unshift("Resolve the red shortage item before accepting any added menu changes.");
  }

  if (plan.intake.budget) {
    actions.push(`Keep food and packaging below ${plan.metrics.costPerGuest} per guest unless manager approves the exception.`);
  }

  return actions;
}

function buildDecision(guestCount, constraints, items) {
  const risk = Math.min(95, 30 + Math.max(0, guestCount - 80) / 2 + constraints.length * 7 + Math.max(0, items.length - 4) * 4);
  if (risk >= 75) return { label: "Accept with rescue controls", score: Math.round(risk) };
  if (constraints.length >= 3) return { label: "Accept after constraint check", score: Math.round(risk) };
  if (items.length === 0) return { label: "Clarify menu before prep", score: Math.round(risk) };
  return { label: "Ready for controlled prep", score: Math.round(risk) };
}

function createPlan(rawInput) {
  const cleanInput = rawInput.trim();
  const guestCount = guestCountFromText(cleanInput) || Math.max(35, Math.min(220, cleanInput.split(/\s+/).length * 2));
  const budget = moneyFromText(cleanInput);
  const menuItems = extractMenuItems(cleanInput);
  const constraints = extractConstraints(cleanInput);
  const decision = buildDecision(guestCount, constraints, menuItems);
  const serviceStyle = detectServiceStyle(cleanInput);
  const now = new Date();
  const id = `PD-${now.toISOString().slice(0, 10).replace(/-/g, "")}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
  const eventName = extractEventName(cleanInput);
  const stations = buildStations(menuItems, guestCount);
  const metrics = {
    guestCount,
    menuCount: menuItems.length,
    bufferPortions: Math.ceil(guestCount * 1.08),
    stationCount: stations.length,
    riskScore: decision.score,
    budget,
    costPerGuest: budget ? `$${(budget / guestCount).toFixed(2)}` : "Budget not found",
  };

  const plan = {
    id,
    eventName,
    createdAt: now.toISOString(),
    rawInput: cleanInput,
    intake: {
      eventName,
      serviceDate: extractServiceDate(cleanInput),
      serviceStyle,
      guestCount,
      budget,
      menuItems,
      constraints,
    },
    decision,
    metrics,
    timeline: buildTimeline(menuItems, constraints, serviceStyle),
    stations,
    shortageWatch: buildShortageWatch(menuItems, constraints, guestCount),
    kitchenBrief: [
      `${eventName} needs ${metrics.bufferPortions} prepared portions for ${guestCount} guests.`,
      `Service style: ${serviceStyle}. Service date: ${extractServiceDate(cleanInput)}.`,
      `Decision: ${decision.label}. Risk score: ${decision.score}/100.`,
      constraints.length ? `Constraints to call out: ${constraints.join(", ")}.` : "No explicit constraints were found; confirm allergens and delivery access.",
    ],
    packoutChecklist: [
      "Count finished portions against the buffer target.",
      "Separate allergen-sensitive meals and mark them before loading.",
      "Confirm hot/cold holding plan and staging order.",
      "Photograph packed order and final count sheet before handoff.",
    ],
    customerConfirmation: `We have your ${guestCount}-guest order for ${extractServiceDate(cleanInput)}. Our kitchen plan is locked around ${serviceStyle.toLowerCase()}, labeled allergen handling, and a final packout count before handoff.`,
  };

  return {
    ...plan,
    nextActions: buildNextActions(plan),
  };
}

function formatDate(value) {
  try {
    return new Intl.DateTimeFormat("en", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(value));
  } catch (error) {
    return value;
  }
}

function useSavedPlans() {
  const [plans, setPlans] = useState(() => safeReadPlans());
  const canPersist = useMemo(storageAvailable, []);

  const savePlan = (plan) => {
    const next = [plan, ...plans.filter((item) => item.id !== plan.id)].slice(0, 24);
    setPlans(next);
    if (canPersist) safeWritePlans(next);
  };

  useEffect(() => {
    if (canPersist) setPlans(safeReadPlans());
  }, [canPersist]);

  return { plans, savePlan, canPersist };
}

function Header() {
  return (
    <header className="pd-header">
      <Link className="pd-brand" to="/" aria-label="PrepDeck home">
        <span className="pd-mark">P</span>
        <span>PrepDeck</span>
      </Link>
      <nav className="pd-nav" aria-label="Main navigation">
        <Link to="/">
          <HomeOutlined /> Workbench
        </Link>
        <Link to="/history">
          <HistoryOutlined /> History
        </Link>
        <Link to="/pricing">
          <DollarOutlined /> Pricing
        </Link>
      </nav>
    </header>
  );
}

function Metric({ label, value }) {
  return (
    <div className="pd-metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function Workbench({ plans, savePlan, canPersist }) {
  const [input, setInput] = useState(DEFAULT_INPUT);
  const [error, setError] = useState("");
  const [isBuilding, setIsBuilding] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = (event) => {
    event.preventDefault();
    const currentInput = input.trim();
    if (currentInput.length < 40) {
      setError("Paste the order, event note, or prep request so PrepDeck can create a real plan.");
      return;
    }

    setError("");
    setIsBuilding(true);
    window.setTimeout(() => {
      const plan = createPlan(currentInput);
      savePlan(plan);
      setIsBuilding(false);
      navigate(`/plans/${plan.id}`);
    }, 450);
  };

  const latest = plans[0];

  return (
    <main>
      <section className="pd-hero">
        <div className="pd-hero-overlay" />
        <div className="pd-hero-inner">
          <div className="pd-hero-copy">
            <span className="pd-eyebrow">Restaurant operations</span>
            <h1>PrepDeck</h1>
            <p>
              Paste a catering order or messy event note. PrepDeck turns it into a saved prep timeline,
              station plan, shortage watch, and customer handoff packet in one move.
            </p>
            <div className="pd-hero-stats" aria-label="Product strengths">
              <Metric label="Mandatory fields" value="1" />
              <Metric label="Post-start clicks" value="0" />
              <Metric label="Saved outputs" value={String(plans.length)} />
            </div>
          </div>
        </div>
      </section>

      <section className="pd-workbench" aria-label="Prep plan workbench">
        <div className="pd-panel pd-input-panel">
          <div className="pd-panel-heading">
            <div>
              <span className="pd-kicker">Start here</span>
              <h2>Paste the current order</h2>
            </div>
            <MenuOutlined className="pd-heading-icon" />
          </div>
          <form onSubmit={handleSubmit}>
            <label htmlFor="prepdeck-input">
              Order, menu, count, constraints, budget, pickup notes, or staff notes
            </label>
            <textarea
              id="prepdeck-input"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder={INPUT_PLACEHOLDER}
              rows={12}
              spellCheck="true"
            />
            {error ? (
              <div className="pd-alert pd-alert-error" role="alert">
                <ExclamationCircleOutlined /> {error}
              </div>
            ) : null}
            {!canPersist ? (
              <div className="pd-alert pd-alert-warning" role="status">
                <ExclamationCircleOutlined /> Browser storage is unavailable, so reopening saved plans is disabled.
              </div>
            ) : null}
            <button className="pd-primary" type="submit" disabled={isBuilding}>
              {isBuilding ? <ReloadOutlined spin /> : <ThunderboltOutlined />}
              {isBuilding ? "Building prep plan" : "Build prep plan"}
            </button>
          </form>
        </div>

        <div className="pd-panel pd-output-panel">
          <div className="pd-panel-heading">
            <div>
              <span className="pd-kicker">Output</span>
              <h2>What is produced immediately</h2>
            </div>
            <CheckCircleOutlined className="pd-heading-icon" />
          </div>
          <div className="pd-output-grid">
            {["Prep timeline", "Station assignments", "Shortage watch", "Kitchen brief", "Packout checklist", "Customer confirmation"].map((item) => (
              <div className="pd-output-chip" key={item}>
                <CheckCircleOutlined /> {item}
              </div>
            ))}
          </div>
          {latest ? (
            <article className="pd-latest">
              <span className="pd-kicker">Latest saved plan</span>
              <h3>{latest.eventName}</h3>
              <p>{latest.decision.label} for {latest.metrics.guestCount} guests.</p>
              <Link className="pd-secondary" to={`/plans/${latest.id}`}>
                <FileTextOutlined /> Open {latest.id}
              </Link>
            </article>
          ) : (
            <div className="pd-empty">
              <ClockCircleOutlined />
              <h3>No saved plans yet</h3>
              <p>Your first prep plan appears here and in history after one processing action.</p>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}

function PlanSummary({ plan }) {
  return (
    <section className="pd-detail-grid">
      <div className="pd-panel pd-decision">
        <span className="pd-kicker">Decision</span>
        <h2>{plan.decision.label}</h2>
        <p>
          Risk score {plan.decision.score}/100. Built from the pasted order for {plan.intake.guestCount} guests,
          {plan.intake.menuItems.length ? ` ${plan.intake.menuItems.length} menu lines,` : " an unclear menu,"} and
          {plan.intake.constraints.length ? ` ${plan.intake.constraints.length} constraints.` : " no explicit constraints."}
        </p>
      </div>
      <Metric label="Plan ID" value={plan.id} />
      <Metric label="Guests" value={plan.metrics.guestCount} />
      <Metric label="Buffer portions" value={plan.metrics.bufferPortions} />
      <Metric label="Cost per guest" value={plan.metrics.costPerGuest} />
    </section>
  );
}

function Timeline({ plan }) {
  return (
    <section className="pd-panel">
      <div className="pd-panel-heading">
        <div>
          <span className="pd-kicker">Prep timeline</span>
          <h2>Timed work plan</h2>
        </div>
        <ClockCircleOutlined className="pd-heading-icon" />
      </div>
      <div className="pd-timeline">
        {plan.timeline.map((item) => (
          <article className="pd-timeline-item" key={`${item.time}-${item.task}`}>
            <strong>{item.time}</strong>
            <div>
              <span>{item.owner}</span>
              <h3>{item.task}</h3>
              <p>{item.reason}</p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function Stations({ plan }) {
  return (
    <section className="pd-panel">
      <div className="pd-panel-heading">
        <div>
          <span className="pd-kicker">Station plan</span>
          <h2>Ownership by work area</h2>
        </div>
        <MenuOutlined className="pd-heading-icon" />
      </div>
      <div className="pd-card-grid">
        {plan.stations.map((station) => (
          <article className="pd-card" key={station.id}>
            <span>{station.owner}</span>
            <h3>{station.name}</h3>
            <p>{station.output}</p>
            <small>{station.target}</small>
            <small>{station.check}</small>
          </article>
        ))}
      </div>
    </section>
  );
}

function ShortageWatch({ plan }) {
  return (
    <section className="pd-panel">
      <div className="pd-panel-heading">
        <div>
          <span className="pd-kicker">Shortage watch</span>
          <h2>Risks to close before production</h2>
        </div>
        <ExclamationCircleOutlined className="pd-heading-icon" />
      </div>
      <div className="pd-risk-list">
        {plan.shortageWatch.map((risk) => (
          <article className="pd-risk" key={risk.item}>
            <span className={`pd-severity pd-severity-${risk.severity.toLowerCase()}`}>{risk.severity}</span>
            <div>
              <h3>{risk.item}</h3>
              <p>{risk.trigger}</p>
              <strong>{risk.action}</strong>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function ReadyPacket({ plan }) {
  const [copied, setCopied] = useState(false);
  const packetText = [
    "Kitchen brief",
    ...plan.kitchenBrief,
    "",
    "Packout checklist",
    ...plan.packoutChecklist,
    "",
    "Customer confirmation",
    plan.customerConfirmation,
    "",
    "Next actions",
    ...plan.nextActions,
  ].join("\n");

  const copyPacket = async () => {
    try {
      await navigator.clipboard.writeText(packetText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch (error) {
      setCopied(false);
    }
  };

  return (
    <section className="pd-panel">
      <div className="pd-panel-heading">
        <div>
          <span className="pd-kicker">Ready packet</span>
          <h2>Prepared summary and handoff</h2>
        </div>
        <button className="pd-icon-button" type="button" title="Copy packet" aria-label="Copy packet" onClick={copyPacket}>
          <CopyOutlined />
        </button>
      </div>
      {copied ? <div className="pd-alert pd-alert-success">Copied packet text.</div> : null}
      <div className="pd-ready-grid">
        <article>
          <h3>Kitchen brief</h3>
          <ul>
            {plan.kitchenBrief.map((item) => <li key={item}>{item}</li>)}
          </ul>
        </article>
        <article>
          <h3>Packout checklist</h3>
          <ul>
            {plan.packoutChecklist.map((item) => <li key={item}>{item}</li>)}
          </ul>
        </article>
        <article>
          <h3>Customer confirmation</h3>
          <p>{plan.customerConfirmation}</p>
        </article>
        <article>
          <h3>Next actions</h3>
          <ol>
            {plan.nextActions.map((item) => <li key={item}>{item}</li>)}
          </ol>
        </article>
      </div>
    </section>
  );
}

function SourceInput({ plan }) {
  return (
    <section className="pd-panel">
      <div className="pd-panel-heading">
        <div>
          <span className="pd-kicker">Consumed input</span>
          <h2>Original order text</h2>
        </div>
        <FileTextOutlined className="pd-heading-icon" />
      </div>
      <pre className="pd-source-text">{plan.rawInput}</pre>
    </section>
  );
}

function DetailPage({ plans }) {
  const { id } = useParams();
  const plan = plans.find((item) => item.id === id);

  if (!plan) {
    return (
      <main className="pd-page pd-page-narrow">
        <div className="pd-panel pd-missing">
          <ExclamationCircleOutlined />
          <h1>Plan not found</h1>
          <p>The saved item is missing from this browser. Open history or build a fresh prep plan.</p>
          <Link className="pd-primary pd-link-button" to="/">
            <ThunderboltOutlined /> Build a plan
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="pd-page">
      <div className="pd-detail-header">
        <div>
          <span className="pd-kicker">Saved prep plan</span>
          <h1>{plan.eventName}</h1>
          <p>{plan.id} created {formatDate(plan.createdAt)}. Service date: {plan.intake.serviceDate}.</p>
        </div>
        <Link className="pd-secondary" to="/history">
          <HistoryOutlined /> View history
        </Link>
      </div>
      <PlanSummary plan={plan} />
      <Timeline plan={plan} />
      <Stations plan={plan} />
      <ShortageWatch plan={plan} />
      <ReadyPacket plan={plan} />
      <SourceInput plan={plan} />
    </main>
  );
}

function HistoryPage({ plans }) {
  return (
    <main className="pd-page">
      <div className="pd-detail-header">
        <div>
          <span className="pd-kicker">History</span>
          <h1>Saved prep plans</h1>
          <p>Reopen any completed plan from this browser.</p>
        </div>
        <Link className="pd-primary pd-link-button" to="/">
          <ThunderboltOutlined /> New plan
        </Link>
      </div>

      {plans.length === 0 ? (
        <div className="pd-panel pd-empty pd-history-empty">
          <ClockCircleOutlined />
          <h2>No saved plans yet</h2>
          <p>Paste one order and PrepDeck will save the full prep packet here.</p>
        </div>
      ) : (
        <section className="pd-history-list">
          {plans.map((plan) => (
            <Link className="pd-history-row" to={`/plans/${plan.id}`} key={plan.id}>
              <div>
                <span>{plan.id}</span>
                <h2>{plan.eventName}</h2>
                <p>{plan.decision.label} for {plan.metrics.guestCount} guests. Created {formatDate(plan.createdAt)}.</p>
              </div>
              <div className="pd-history-metrics">
                <Metric label="Risk" value={`${plan.metrics.riskScore}/100`} />
                <Metric label="Stations" value={plan.metrics.stationCount} />
              </div>
            </Link>
          ))}
        </section>
      )}
    </main>
  );
}

function PricingPage() {
  const tiers = [
    {
      name: "Rush",
      price: "$49",
      detail: "Per high-pressure catering plan",
      points: ["One saved prep packet", "Timeline and station plan", "Shortage watch", "Copy-ready handoff"],
    },
    {
      name: "Kitchen",
      price: "$199",
      detail: "Per month for recurring catering teams",
      points: ["Unlimited saved plans", "History and reopening", "Reusable prep packets", "Priority support"],
      featured: true,
    },
    {
      name: "Group",
      price: "$499",
      detail: "Per month for multi-site operators",
      points: ["Site-by-site planning", "Shared prep standards", "Manager review rhythm", "Launch assistance"],
    },
  ];

  return (
    <main className="pd-page">
      <div className="pd-detail-header">
        <div>
          <span className="pd-kicker">Commercial fit</span>
          <h1>Pricing for kitchens that cannot miss the pickup window</h1>
          <p>PrepDeck is priced around avoided rush waste, refunded orders, staff confusion, and manager time.</p>
        </div>
      </div>
      <section className="pd-pricing-grid">
        {tiers.map((tier) => (
          <article className={`pd-price-card${tier.featured ? " pd-price-featured" : ""}`} key={tier.name}>
            <span>{tier.name}</span>
            <h2>{tier.price}</h2>
            <p>{tier.detail}</p>
            <ul>
              {tier.points.map((point) => <li key={point}>{point}</li>)}
            </ul>
            <Link className={tier.featured ? "pd-primary pd-link-button" : "pd-secondary"} to="/">
              <ThunderboltOutlined /> Try the workbench
            </Link>
          </article>
        ))}
      </section>
    </main>
  );
}

function PrepDeckShell() {
  const { plans, savePlan, canPersist } = useSavedPlans();

  return (
    <div className="pd-app">
      <Header />
      <Routes>
        <Route path="/" element={<Workbench plans={plans} savePlan={savePlan} canPersist={canPersist} />} />
        <Route path="/plans/:id" element={<DetailPage plans={plans} />} />
        <Route path="/history" element={<HistoryPage plans={plans} />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <footer className="pd-footer">
        <span>PrepDeck by Agentic Wings</span>
        <span>Public first version. No external provider connection required.</span>
      </footer>
    </div>
  );
}

export default function PrepDeckApp() {
  return (
    <BrowserRouter>
      <PrepDeckShell />
    </BrowserRouter>
  );
}
